import { LanguageKeys } from 'src/common/constants/languages';
import { IColumnsBlock, IImageBlock, IPageBlock, ITextBlock } from 'src/interfaces/PageBlock';
import { imageBlockToPageImages } from 'src/helpers/pageBlockImageUtils';

export interface PageImage {
  src: string;
  alt: string;
}

function resolveTextHtml(i18n: ITextBlock['i18n'], lang: LanguageKeys): string {
  const raw = i18n[lang] ?? i18n.en;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && 'html' in raw) {
    return String((raw as { html?: string }).html ?? '');
  }
  return '';
}

/** Extract img src/alt pairs from a TinyMCE HTML string (document order). */
export function extractImagesFromHtml(html: string): PageImage[] {
  if (!html.trim()) return [];

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll('img[src]')).map(img => ({
    src: img.getAttribute('src') ?? '',
    alt: img.getAttribute('alt') ?? '',
  })).filter(image => image.src.length > 0);
}

function dedupeImages(images: PageImage[]): PageImage[] {
  const seen = new Set<string>();
  return images.filter(image => {
    if (seen.has(image.src)) return false;
    seen.add(image.src);
    return true;
  });
}

function walkBlocks(blocks: IPageBlock[], lang: LanguageKeys, images: PageImage[]): void {
  for (const block of blocks) {
    if (block.type === 'text') {
      images.push(...extractImagesFromHtml(resolveTextHtml(block.i18n, lang)));
      continue;
    }

    if (block.type === 'image') {
      images.push(...imageBlockToPageImages(block as IImageBlock, lang));
      continue;
    }

    if (block.type === 'columns') {
      for (const column of (block as IColumnsBlock).columns) {
        walkBlocks(column.blocks, lang, images);
      }
    }
  }
}

/** Collect unique TinyMCE images from page blocks in reading order. */
export function collectImagesFromPageBlocks(
  blocks: IPageBlock[],
  lang: LanguageKeys,
): PageImage[] {
  const images: PageImage[] = [];
  walkBlocks(blocks, lang, images);
  return dedupeImages(images);
}
